import * as React from 'react'

import { localPaymentMethodMap } from '@thg-commerce/enterprise-config/paymentMethod'
import {
  i18n,
  useFormattableI18nProperty,
  useLogger,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { ProductPageData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  styled,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { SafeHtml } from '@thg-commerce/gravity-elements'
import { PaymentMethods } from '@thg-commerce/gravity-patterns/PaymentMethods'
import { PaymentMethodsProps } from '@thg-commerce/gravity-patterns/PaymentMethods/PaymentMethods'
import { mq, spacing, Text } from '@thg-commerce/gravity-theme'

export interface BuyNowPayLaterPaymentOptionsProps {
  providersContent: ProductPageData['variants'][0]['buyNowPayLaterProviders']
}

const BnplTextContainer = styled.p`
  margin-left: ${spacing(1)};
  max-width: 231px;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin-top: ${spacing(1)};
    max-width: 100%;
    width: auto;
    height: auto;
  }
`

const BnplProviderLinkContainer = styled.a`
  ${(props) =>
    Text(
      props.theme.pageTheme.buyNowPayLater.readMoreLink?.font.entry ||
        'bodyText',
      props.theme.pageTheme.buyNowPayLater.readMoreLink?.font.style ||
        'default',
    )}

  margin-left: ${spacing(0.5)};
  color: ${(props) => props.theme.colors.palette.brand.base};
`

export const BuyNowPayLaterPaymentOptions = (
  props: BuyNowPayLaterPaymentOptionsProps,
) => {
  const logger = useLogger()
  const theme = useTheme()
  const { openBuyNowPayLaterLinkInNewTab } = useSiteConfig()

  const paymentMethodsI18nText = {
    afterPay: i18n('paymentmethods.afterpay.svg.title'),
    alipay: i18n('paymentmethods.alipay.svg.title'),
    amazonpay: i18n('paymentmethods.amazonpay.svg.title'),
    alipayPlus: i18n('paymentmethods.alipay.plus.svg.title'),
    atome: i18n('paymentmethods.atome.svg.title'),
    americanExpress: i18n('paymentmethods.american.express.svg.title'),
    applePay: i18n('paymentmethods.apple.pay.svg.title'),
    arvato: i18n('paymentmethods.arvato.svg.title'),
    bancontact: i18n('paymentmethods.bancontact.svg.title'),
    clearpay: i18n('paymentmethods.clearpay.svg.title'),
    dinersClub: i18n('paymentmethods.diners.club.svg.title'),
    discover: i18n('paymentmethods.discover.svg.title'),
    elo: i18n('paymentmethods.elo.svg.title'),
    googlePay: i18n('paymentmethods.google.pay.svg.title'),
    hiper: i18n('paymentmethods.hiper.svg.title'),
    ideal: i18n('paymentmethods.ideal.svg.title'),
    interac: i18n('paymentmethods.interac.svg.title'),
    jcb: i18n('paymentmethods.jcb.svg.title'),
    kcp: i18n('paymentmethods.kcp.svg.title'),
    klarna: i18n('paymentmethods.klarna.svg.title'),
    laybuy: i18n('paymentmethods.laybuy.svg.title'),
    maestro: i18n('paymentmethods.maestro.svg.title'),
    mastercard: i18n('paymentmethods.mastercard.svg.title'),
    molpay: i18n('paymentmethods.molpay.svg.title'),
    multibanco: i18n('paymentmethods.multibanco.svg.title'),
    one4All: i18n('paymentmethods.one4all.svg.title'),
    openpay: i18n('paymentmethods.openpay.svg.title'),
    paypal: i18n('paymentmethods.paypal.svg.title'),
    paytm: i18n('paymentmethods.paytm.svg.title'),
    primeiropay: i18n('paymentmethods.primeiropay.svg.title'),
    rupay: i18n('paymentmethods.rupay.svg.title'),
    sepa: i18n('paymentmethods.sepa.svg.title'),
    solo: i18n('paymentmethods.solo.svg.title'),
    splitIt: i18n('paymentmethods.splitit.svg.title'),
    spotii: i18n('paymentmethods.spotii.svg.title'),
    tenpay: i18n('paymentmethods.tenpay.svg.title'),
    trustPay: i18n('paymentmethods.trust.pay.svg.title'),
    unionpay: i18n('paymentmethods.unionpay.svg.title'),
    visaDebit: i18n('paymentmethods.visa.debit.svg.title'),
    visaCredit: i18n('paymentmethods.visa.credit.svg.title'),
    visaElectron: i18n('paymentmethods.visa.electron.svg.title'),
    webMoney: i18n('paymentmethods.web.money.svg.title'),
    wechatPay: i18n('paymentmethods.wechat.pay.svg.title'),
    yandex: i18n('paymentmethods.yandex.svg.title'),
    zippay: i18n('paymentmethods.zippay.svg.title'),
    humm: i18n('paymentmethods.humm.svg.title'),
    quadpay: i18n('paymentmethods.quadpay.svg.title'),
    sezzle: i18n('paymentmethods.sezzle.svg.title'),
    newpay: i18n('paymentmethods.newpay.svg.title'),
    upi: i18n('paymentmethods.upi.svg.title'),
    frasersPlus: i18n('paymentmethods.frasersplus.svg.title'),
  }

  const i18nText = {
    withText: i18n('product.buynowpaylaterproviders.with'),
    learnMoreText: i18n('product.buynowpaylaterproviders.learnmore'),
    learnMoreLink: useFormattableI18nProperty(
      'product.buynowpaylater.learnmore.link',
    ),
    afterpayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.afterpay',
    ),
    clearpayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.clearpay',
    ),
    openpayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.openpay',
    ),
    splititPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.splitit',
    ),
    klarnaPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.klarna',
    ),
    klarna_slicePaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.klarna_slice',
    ),
    frasers_plusPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.frasers_plus',
    ),
    quadpayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.quadpay',
    ),
    zippayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.zippay',
    ),
    hummPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.humm',
    ),
    paypal_in_3PaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.paypal_in_3',
    ),
    laybuyPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.laybuy',
    ),
    sezzlePaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.sezzle',
    ),
    atomPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.atom',
    ),
    newpayPaymentText: useFormattableI18nProperty(
      'product.buynowpaylaterproviders.instalmentsof.newpay',
    ),
    afterpayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.afterpay',
    ),
    clearpayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.clearpay',
    ),
    openpayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.openpay',
    ),
    splititDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.splitit',
    ),
    klarna_sliceDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.klarna_slice',
    ),
    klarnaDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.klarna',
    ),
    quadpayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.quadpay',
    ),
    zippayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.zippay',
    ),
    hummDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.humm',
    ),
    paypal_in_3DisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.paypal_in_3',
    ),
    laybuyDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.laybuy',
    ),
    sezzleDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.sezzle',
    ),
    atomDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.atom',
    ),
    newpayDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.newpay',
    ),
    frasers_plusDisplayNameText: i18n(
      'product.buynowpaylaterproviders.displayname.frasers_plus',
    ),
  }

  const providersList = (props.providersContent || []).map((provider) => {
    const providerName = provider.providerName.toUpperCase()
    if (typeof localPaymentMethodMap[providerName] !== 'undefined') {
      return localPaymentMethodMap[providerName]['icon'].toUpperCase()
    }
    return providerName
  })

  const providerDescriptions = (props.providersContent || []).reduce<
    Required<PaymentMethodsProps>['paymentDescriptions']
  >((accumulator, provider) => {
    if (!i18nText[`${provider.providerName}PaymentText`]) {
      logger.warn(
        `[BuyNowPayLayerPaymentOptions]: Missing payment text for provider ${provider.providerName}`,
      )
      return accumulator
    }

    let providerTitle = provider.providerName.toUpperCase()
    if (typeof localPaymentMethodMap[providerTitle] !== 'undefined') {
      providerTitle = localPaymentMethodMap[providerTitle]['icon'].toUpperCase()
    }

    // Big hack!
    const displayValue =
      theme.pageTheme.buyNowPayLater.price?.font.style === 'alternate'
        ? `<strong>${provider.instalmentAmount.displayValue}</strong>`
        : provider.instalmentAmount.displayValue

    accumulator.push({
      providerName: providerTitle,
      providerDescription: (
        <BnplTextContainer>
          <SafeHtml
            content={i18nText[`${provider.providerName}PaymentText`]([
              provider.numberOfInstalments.toString(),
              displayValue,
              i18nText.withText,
              i18nText[`${provider.providerName}DisplayNameText`],
            ])}
            renderedAs="span"
          />
          <BnplProviderLinkContainer
            title={i18nText.learnMoreLink(
              i18nText[`${provider.providerName}DisplayNameText`],
            )}
            href={provider.landingPageLink}
            target={openBuyNowPayLaterLinkInNewTab ? '_blank' : ''}
          >
            {i18nText.learnMoreText}
          </BnplProviderLinkContainer>
        </BnplTextContainer>
      ),
    })
    return accumulator
  }, [])

  return (
    <PaymentMethods
      text={' '}
      paymentProviders={providersList}
      paymentMethodsI18nText={paymentMethodsI18nText}
      paymentDescriptions={providerDescriptions}
      disableTagLine={true}
      bnplIcons={true}
      direction="column"
    />
  )
}
